import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { UpsellingPopover as F1UpsellingPopover } from '@factorialco/f0-react'
import { AvailableModules, Features } from 'generated/featureDefinitions'

import { track } from 'modules/core/lib/Tracking'
import { trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useMarketplaceModulesPath } from 'modules/marketplace_modules/hooks/useMarketplaceModulesPath'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellGuard } from 'modules/upselling_kit/components/UpsellGuard'
import { useGetUpsellingData } from 'modules/upselling_kit/hooks/useGetUpsellingData'
import { useUpsell } from 'modules/upselling_kit/lib/upsellContext'
import { UpsellingKitModule } from 'modules/upselling_kit/types'
import {
  buildUpsellOriginParam,
  handleUpsellingLearnMore,
  handleUpsellingVisibility,
} from 'modules/upselling_kit/utils/handlers'

type Side = 'top' | 'bottom' | 'left' | 'right'

type ProductWidgetPopoverBaseProps = {
  label?: string
  mediaUrl: string
  requestSource: string
  moduleRequested: AvailableModules
  requestReason: ValidReasons
  featureRequested?: Features
  pageSource?: string
  variant: 'outlinePromote' | 'outline' | 'ghost'
  title?: string
  description?: string
  size?: 'sm' | 'md' | 'lg'
  hideLabel?: boolean
  side?: Side
}

type StandaloneProps = ProductWidgetPopoverBaseProps & UpsellingKitModule

const ProductWidgetPopoverContent = ({
  mediaUrl,
  label,
  variant,
  title,
  description,
  size = 'md',
  hideLabel,
  side,
}: {
  mediaUrl: string
  label?: string
  variant: 'outlinePromote' | 'outline' | 'ghost'
  title?: string
  description?: string
  size?: 'sm' | 'md' | 'lg'
  hideLabel?: boolean
  side?: Side
}) => {
  const [isOpen, toggleOpen] = useState(false)
  const navigate = useNavigate()
  const { getDetailPath } = useMarketplaceModulesPath()
  const { requestSource, moduleRequested, upsellData } = useUpsell()
  const { moduleSlug } = useGetUpsellingData({
    moduleRequested,
    title,
  })

  const handleRequest = () => {
    track(trackingEvents.upsellingKit.clickProductUpsellComponent, {
      source: requestSource,
      component: 'upselling_popover',
      module: moduleRequested,
    })

    handleUpsellingLearnMore({
      moduleRequested,
      requestSource,
      component: 'upselling_popover',
    })

    const upsellOriginParam = buildUpsellOriginParam(
      'upselling_popover',
      requestSource,
      'Upselling Kit'
    )

    navigate(getDetailPath(moduleSlug || '', upsellOriginParam))
    toggleOpen(false)
  }

  useEffect(() => {
    handleUpsellingVisibility({ moduleRequested, requestSource, component: 'upselling_popover' })
  }, [moduleRequested, requestSource])

  useEffect(() => {
    if (isOpen) {
      track(trackingEvents.upsellingKit.viewedProductUpsellComponent, {
        source: requestSource,
        component: 'upselling_popover',
        module: moduleRequested,
      })
    }
  }, [isOpen, requestSource, moduleRequested])

  return (
    <F1UpsellingPopover
      isOpen={isOpen}
      setIsOpen={toggleOpen}
      side={side}
      label={label || ''}
      hideLabel={hideLabel}
      variant={variant}
      mediaUrl={mediaUrl}
      size={size}
      title={title || upsellData.upsellModalTitle || ''}
      description={description || upsellData.upsellModalDescription || ''}
      actions={[
        {
          type: 'regular',
          label: i18n.t('upselling_kit.button.learn_more'),
          variant: 'promote',
          onClick: handleRequest,
        },
      ]}
    ></F1UpsellingPopover>
  )
}

export const ProductWidgetPopover = ({
  mediaUrl,
  label,
  variant,
  title,
  description,
  fallbackComponent,
  size,
  hideLabel,
  side,
  ...upsellProps
}: StandaloneProps) => {
  return (
    <UpsellGuard {...upsellProps} fallbackComponent={fallbackComponent || null}>
      <ProductWidgetPopoverContent
        mediaUrl={mediaUrl}
        label={label}
        variant={variant}
        title={title}
        description={description}
        size={size}
        hideLabel={hideLabel}
        side={side}
      />
    </UpsellGuard>
  )
}
